import { BadgeCheck } from 'lucide-react';

interface VerifiedMemberBadgeProps {
    /** Date the professional's profile went live (ISO / `YYYY-MM-DD…`). */
    profileLiveDate: string;
}

/** Extracts the calendar year from a profile live date string. */
const getYearJoined = (profileLiveDate: string): string =>
    profileLiveDate.substring(0, 4);

/** Calculates the number of full calendar years since the given year. */
const getMembershipYears = (yearJoined: string): number =>
    new Date().getFullYear() - Number(yearJoined);

/** Formats the membership duration as a human-readable label. */
const formatMembershipLabel = (years: number): string => {
    if (years < 1) {
        return 'New';
    }

    return years === 1 ? '1 year' : `${String(years)} years`;
};

/**
 * Formats the "Since …" suffix.
 * For new members, includes the full month name when the month can be parsed;
 * otherwise falls back to the year only.
 */
export const formatSinceLabel = (
    profileLiveDate: string,
    isNew: boolean,
): string => {
    const yearJoined = getYearJoined(profileLiveDate);
    if (!isNew || !/^\d{4}$/.test(yearJoined)) {
        return yearJoined;
    }

    // Read month from the `YYYY-MM` prefix so timezone offsets cannot shift the month.
    const monthMatch = /^(\d{4})-(\d{2})/.exec(profileLiveDate);
    if (!monthMatch) {
        return yearJoined;
    }

    const monthIndex = Number(monthMatch[2]) - 1;
    if (monthIndex < 0 || monthIndex > 11) {
        return yearJoined;
    }

    const monthName = new Intl.DateTimeFormat('en-NZ', {
        month: 'long',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(Number(monthMatch[1]), monthIndex, 1)));

    return `${monthName} ${monthMatch[1]}`;
};

/**
 * Displays a trust badge indicating verified ArchiPro membership
 * with the duration and when they joined.
 */
const VerifiedMemberBadge = ({ profileLiveDate }: VerifiedMemberBadgeProps) => {
    const yearJoined = getYearJoined(profileLiveDate);
    const membershipYears = getMembershipYears(yearJoined);
    const membershipLabel = formatMembershipLabel(membershipYears);
    const sinceLabel = formatSinceLabel(profileLiveDate, membershipYears < 1);

    return (
        <div
            className="flex items-center gap-4 rounded-2 bg-black/5 px-6 py-5"
            data-testid="member-since"
        >
            <BadgeCheck
                className="size-7 shrink-0 text-gray-500"
                strokeWidth={1.5}
            />
            <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium tracking-[0.08em] uppercase">
                    Verified ArchiPro Member
                </span>
                <span className="text-sm tracking-[-0.01em] text-gray-500">
                    <span className="font-medium">{membershipLabel}</span>{' '}
                    &middot; Since {sinceLabel}
                </span>
            </div>
        </div>
    );
};

export default VerifiedMemberBadge;
